import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Header() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-background-50/95 backdrop-blur-xl border-b border-background-200/70">
        <div className="mx-auto flex items-center justify-between px-4 md:px-10 h-[64px] md:h-[70px] max-w-[1440px]">
          <Link
            to="/"
            className="text-base md:text-lg font-heading font-semibold text-foreground-950 whitespace-nowrap tracking-tight flex-shrink-0"
          >
            Mulebuy <span className="font-light italic">Spreadsheet</span>
          </Link>

          <nav className="hidden md:flex items-center gap-2 lg:gap-3">
            <Link
              to="/"
              className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors rounded-lg hover:bg-background-100 whitespace-nowrap cursor-pointer"
            >
              Home
            </Link>

            <Link
              to="/mulebuy-spreadsheet-complete-guide"
              className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors rounded-lg hover:bg-background-100 whitespace-nowrap cursor-pointer"
            >
              Shopping Guide
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                data-category-dropdown
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-foreground-950 text-background-50 text-sm font-medium cursor-pointer hover:bg-foreground-800 transition-colors whitespace-nowrap"
              >
                <span>All Categories</span>
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-300 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 right-0 w-64 bg-background-50 border border-background-200/70 rounded-2xl shadow-lg overflow-hidden z-50 animate-[fadeIn_0.2s_ease-out]">
                  <div className="py-2 max-h-[420px] overflow-y-auto">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-800 hover:bg-background-100 transition-colors group cursor-pointer"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-primary-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"></span>
                        <i className={`${cat.icon} text-base text-foreground-500 group-hover:text-primary-500 transition-colors w-5 h-5 flex items-center justify-center`}></i>
                        <span className="font-medium">{cat.name}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 rounded-full border-2 border-foreground-950 text-foreground-950 text-sm font-semibold cursor-pointer hover:bg-foreground-950 hover:text-background-50 transition-all whitespace-nowrap"
            >
              Mulebuy Spreadsheet
            </a>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold cursor-pointer hover:bg-primary-600 transition-all whitespace-nowrap"
            >
              Buy It Now
              <i className="ri-arrow-right-line text-base"></i>
            </a>
          </nav>

          <button
            onClick={() => setMobileMenuOpen(true)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-950 cursor-pointer hover:bg-background-100 transition-colors"
            aria-label="Open menu"
          >
            <i className="ri-menu-line text-xl"></i>
          </button>
        </div>
      </header>

      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-[9999]">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={closeMobileMenu}
          />

          <div className="absolute top-0 right-0 bottom-0 w-[85%] max-w-[360px] bg-background-50 overflow-y-auto animate-[slideInRight_0.25s_ease-out]">
            <div className="sticky top-0 z-10 flex items-center justify-between px-4 h-[64px] bg-background-50/95 backdrop-blur-xl border-b border-background-200/70">
              <span className="text-sm font-semibold text-foreground-500">Menu</span>
              <button
                onClick={closeMobileMenu}
                className="w-10 h-10 flex items-center justify-center rounded-lg text-foreground-950 cursor-pointer hover:bg-background-100 transition-colors"
                aria-label="Close menu"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <nav className="px-4 py-4 space-y-1">
              <Link
                to="/"
                onClick={closeMobileMenu}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground-950 font-medium hover:bg-background-100 transition-colors cursor-pointer"
              >
                <span className="w-8 h-8 rounded-lg bg-foreground-100 flex items-center justify-center">
                  <i className="ri-home-line text-lg text-foreground-700"></i>
                </span>
                Home
              </Link>

              <Link
                to="/mulebuy-spreadsheet-complete-guide"
                onClick={closeMobileMenu}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground-950 font-medium hover:bg-background-100 transition-colors cursor-pointer"
              >
                <span className="w-8 h-8 rounded-lg bg-foreground-100 flex items-center justify-center">
                  <i className="ri-book-open-line text-lg text-foreground-700"></i>
                </span>
                Shopping Guide
              </Link>

              <div className="pt-3 pb-1">
                <p className="px-4 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider">Categories</p>
              </div>

              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeMobileMenu}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <span className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center">
                    <i className={`${cat.icon} text-base text-foreground-500`}></i>
                  </span>
                  <span className="text-sm font-medium">{cat.name}</span>
                  <i className="ri-external-link-line text-xs text-foreground-400 ml-auto"></i>
                </a>
              ))}
            </nav>

            <div className="sticky bottom-0 px-4 py-4 space-y-3 bg-background-50 border-t border-background-200/70">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                onClick={closeMobileMenu}
                className="block w-full text-center px-5 py-3 rounded-full border-2 border-foreground-950 text-foreground-950 text-sm font-semibold cursor-pointer hover:bg-foreground-950 hover:text-background-50 transition-all"
              >
                Mulebuy Spreadsheet
              </a>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                onClick={closeMobileMenu}
                className="block w-full text-center px-5 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold cursor-pointer hover:bg-primary-600 transition-all flex items-center justify-center gap-2"
              >
                Buy It Now
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}